import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { MAGIC_INSIGHTS_CREDITS_FEATURE_NAME } from '../../../classes/magic-insights-credits';
import { CompanyService } from '../../../services/company.service';
import { DataSharingService } from '../../../services/data-sharing.service';
import { MessageService } from '../../../services/message.service';
import { DashboardBulkSelection, selectionCount, selectionIds, selectionParams, validSelectionCount } from '../../../types/dashboard-bulk-selection';

declare var $: any;

export interface GetInsightsModule {
  id: number;
  name: string;
  isOrganisationModule: boolean;
  questionCount?: number;
}

export interface GetInsightsCreditCalculation {
  module: { id: number; name: string; type: 'ready_made' | 'custom'; question_count: number };
  company_count: number;
  ready_made_company_count: number;
  custom_research_answer_count: number;
  already_available_count: number;
  unavailable_company_count: number;
  credits_required: number;
  credits_remaining: number;
  has_enough_credits: boolean;
  timing: string;
}

@Component({
  selector: 'get-insights',
  templateUrl: './get-insights.component.html',
  styleUrl: './get-insights.component.css',
  standalone: false,
})
export class GetInsightsComponent implements OnInit, OnDestroy {
  @Input() selection: DashboardBulkSelection;
  private openedSelection: DashboardBulkSelection;
  @Input() modulesInfo: GetInsightsModule[] = [];
  @Input() numberOfCompaniesMatchingSearchParams: number = null;
  @Input() explicitCompanyIds: number[] = null;
  @Input() modalId = 'getInsightsModal';
  @Output() completed = new EventEmitter<any>();

  readonly featureName = MAGIC_INSIGHTS_CREDITS_FEATURE_NAME;
  readonly maximumCompanies = 200000;

  selectedModuleId: number = null;
  selectedCompanyLimit: number = null;
  companyIds: number[] = [];
  searchParams = new HttpParams();
  creditCalculation: GetInsightsCreditCalculation = null;
  requestId: string = null;
  showModulePicker = true;
  isAwaitingRecalculation = false;

  private subscriptions: Subscription[] = [];
  private creditCalculationRequestSequence = 0;
  private companyLimitChanges = new Subject<void>();

  constructor(
    private dataStore: DataSharingService,
    private companyService: CompanyService,
    private messageService: MessageService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.dataStore.currentSelectedCompanyIds.subscribe(ids => {
        if (this.selection) return;
        this.companyIds = ids || [];
      }),
      this.dataStore.currentSelectedSearchParams.subscribe(params => {
        if (this.selection) return;
        this.searchParams = params || new HttpParams();
      }),
      this.dataStore.currentMagicInsightsUnlockCount.subscribe(count => {
        if (this.selection) return;
        this.selectedCompanyLimit = count;
      }),
      // Recalculate as the limit is typed. Waiting for blur or Enter left the cost
      // looking stale with no indication that anything was required of the user.
      this.companyLimitChanges.pipe(debounceTime(450)).subscribe(() => {
        this.refreshCreditCalculation();
        this.isAwaitingRecalculation = false;
        this.changeDetector.detectChanges();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  open(moduleId: number = null): void {
    this.openedSelection = this.selection;
    if (this.openedSelection) {
      if (this.openedSelection.mode === 'none') return;
      this.companyIds = selectionIds(this.openedSelection);
      this.searchParams = selectionParams(this.openedSelection);
      this.selectedCompanyLimit = selectionCount(this.openedSelection);
    } else if (!this.isManualSelection && this.selectedCompanyLimit == null) {
      this.selectedCompanyLimit = Math.min(this.numberOfCompaniesMatchingSearchParams ?? 0, this.maximumCompanies);
    }
    this.selectedModuleId = moduleId ?? this.modulesInfo[0]?.id ?? null;
    this.showModulePicker = moduleId === null;
    this.isAwaitingRecalculation = false;
    this.requestId = this.newRequestId();
    if (this.selectedModuleId) {
      this.refreshCreditCalculation();
    }
    this.changeDetector.detectChanges();
    $(`#${this.modalId}`).modal('show');
    // Reopening keeps the previous scroll position, which can hide the picker.
    const modalBody = document.querySelector(`#${this.modalId} .modal-body`);
    if (modalBody) modalBody.scrollTop = 0;
  }

  get selectedModule(): GetInsightsModule | null {
    return this.modulesInfo.find(module => module.id === this.selectedModuleId) || null;
  }

  get selectedCompanyIds(): number[] {
    return this.explicitCompanyIds?.length ? this.explicitCompanyIds : this.companyIds;
  }

  get isManualSelection(): boolean {
    return this.selectedCompanyIds.length > 0;
  }

  get canEditCompanyCount(): boolean {
    return !this.isManualSelection && (!this.openedSelection ||
      (this.openedSelection.mode === 'query' && this.openedSelection.scope === 'all_matching'));
  }

  get selectedCompanyCount(): number {
    if (this.openedSelection) return selectionCount(this.openedSelection);
    if (this.isManualSelection) return this.selectedCompanyIds.length;
    return this.selectedCompanyLimit ?? 0;
  }

  get isSelectionValid(): boolean {
    const maximum = this.isManualSelection || (this.openedSelection && !this.canEditCompanyCount)
      ? this.maximumCompanies : Math.min(this.numberOfCompaniesMatchingSearchParams ?? this.maximumCompanies, this.maximumCompanies);
    return validSelectionCount(this.selectedCompanyCount, maximum);
  }

  get maximumCreditCost(): number {
    return this.creditCalculation?.credits_required || 0;
  }

  get projectedCreditBalance(): number {
    if (!this.creditCalculation) return 0;
    return this.creditCalculation.credits_remaining - this.maximumCreditCost;
  }

  get hasReadyMadeModules(): boolean {
    return this.modulesInfo.some(module => !module.isOrganisationModule);
  }

  get hasCustomModules(): boolean {
    return this.modulesInfo.some(module => module.isOrganisationModule);
  }

  get actionableResultCount(): number {
    if (!this.creditCalculation || !this.selectedModule) return 0;
    return this.selectedModule.isOrganisationModule
      ? this.creditCalculation.custom_research_answer_count
      : this.creditCalculation.ready_made_company_count;
  }

  get hasActionableResults(): boolean {
    return this.actionableResultCount > 0;
  }

  get submitButtonLabel(): string {
    if (this.isSelectionTooLarge) return 'Reduce company count';
    if (!this.selectedModule) return 'Choose an insight';
    if (this.creditCalculation && !this.hasActionableResults) return 'Nothing available';
    if (this.creditCalculation && !this.creditCalculation.has_enough_credits) return 'Not enough credits';
    return 'Get insights';
  }

  displayModuleName(module: GetInsightsModule): string {
    return module?.name?.replace(/^Magic Insights\s*[-–—:]\s*/i, '') || '';
  }

  get isSelectionTooLarge(): boolean {
    return this.selectedCompanyCount > this.maximumCompanies;
  }

  get canSubmit(): boolean {
    return !!this.selectedModule && !!this.creditCalculation && this.hasActionableResults &&
      this.creditCalculation.has_enough_credits &&
      this.isSelectionValid && !this.isCalculating && !this.isAwaitingRecalculation && !this.isSubmitting;
  }

  get isSubmitting(): boolean {
    return !!(
      this.companyService.isLoading.loadingUnlockMagicInsightsModules ||
      this.companyService.isLoading.loadingGenerateMagicInsights
    );
  }

  get isCalculating(): boolean {
    return !!this.companyService.isLoading.loadingCalculateMagicInsightsCredits;
  }

  // Deliberately terse: this sits in the right-hand column of an outcome row, so a
  // long sentence here squeezes the label out of its own column.
  get shortTimingLabel(): string {
    if (!this.selectedModule?.isOrganisationModule) return 'Available now';
    if (this.selectedCompanyCount <= 1) return 'Usually within 5 minutes';
    if (this.selectedCompanyCount <= 100) return 'Usually within 30 minutes';
    return 'Starts immediately';
  }

  onCompanyLimitChange(value: number): void {
    if (this.openedSelection && !this.canEditCompanyCount) return;
    this.selectedCompanyLimit = value;
    if (this.openedSelection?.mode === 'query' && this.openedSelection.scope === 'all_matching') {
      this.openedSelection = Object.freeze({ ...this.openedSelection, count: value });
      this.searchParams = selectionParams(this.openedSelection);
    }
    // Drop the previous figures straight away: the header count updates on every
    // keystroke, so leaving them up shows a cost for a company count that has moved on.
    this.creditCalculation = null;
    this.isAwaitingRecalculation = true;
    // The backend treats a repeated requestId as a retry of the same submission and
    // replays its original response, so a changed limit needs a new id or a retried
    // submit would be priced for the new count but fulfilled against the old one.
    this.requestId = this.newRequestId();
    this.companyLimitChanges.next();
  }

  moduleChanged(): void {
    this.requestId = this.newRequestId();
    this.refreshCreditCalculation();
  }

  refreshCreditCalculation(): void {
    const requestSequence = ++this.creditCalculationRequestSequence;
    if (!this.selectedModuleId || !this.isSelectionValid) {
      this.creditCalculation = null;
      return;
    }
    const requestedModuleId = this.selectedModuleId;
    this.creditCalculation = null;
    this.companyService.calculateMagicInsightsCredits(
      requestedModuleId,
      this.isManualSelection ? this.selectedCompanyIds : null,
      this.isManualSelection ? null : this.searchParams,
      this.isManualSelection ? null : this.selectedCompanyCount,
    ).subscribe(calculation => {
      if (
        requestSequence !== this.creditCalculationRequestSequence ||
        requestedModuleId !== this.selectedModuleId ||
        Number(calculation?.module?.id) !== requestedModuleId
      ) return;
      if (calculation?.credits_required === undefined) return;
      this.creditCalculation = calculation;
      this.changeDetector.detectChanges();
    });
  }

  submit(): void {
    if (!this.canSubmit) return;

    const module = this.selectedModule;
    if (module.isOrganisationModule) {
      this.companyService.generateMagicInsights(
        module.id,
        this.searchParams,
        this.isManualSelection ? this.selectedCompanyIds : null,
        // An explicit company selection must never be capped by a limit left over
        // from a different scope (e.g. a page-count preset from another entry point) --
        // the backend truncates the queryset to this count whenever it is set,
        // regardless of which companies were actually chosen.
        this.isManualSelection ? null : this.selectedCompanyLimit,
        this.numberOfCompaniesMatchingSearchParams,
        this.requestId,
        this.creditCalculation.credits_required,
      ).subscribe(response => {
        if (!response.body?.aira_run_id) return;
        this.finish(response, module);
        if (this.selectedCompanyCount !== 1) {
          this.messageService.showInfo(
            'Your Magic Insights research has been submitted. Results will appear automatically when ready.',
          );
        }
      });
      return;
    }

    this.companyService.unlockMagicInsightsModules(
      module.id,
      this.selectedCompanyIds,
      this.isManualSelection ? null : this.searchParams,
      // Same reasoning as generateMagicInsights above: never cap an explicit
      // company selection by a limit that belongs to a different scope.
      this.isManualSelection ? null : this.selectedCompanyLimit,
      this.creditCalculation.credits_required,
      true,
    ).subscribe(response => {
      if (!response?.ok) return;
      this.finish(response, module);
      this.messageService.showInfo('Your ready-made Magic Insights are now available.', 4000);
    });
  }

  private finish(response: any, module: GetInsightsModule): void {
    $(`#${this.modalId}`).modal('hide');
    this.completed.emit({ response, moduleId: module.id, isOrganisationModule: module.isOrganisationModule });
  }

  private newRequestId(): string {
    return `get-insights-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}
